import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getCartRelatedItemList, loadCartPageWithSaveProductCart } from "./../redux/actions/action-creator";

import Preloader from "../helper/Preloader";
import ColorInit from "../helper/ColorInit";
import Header from "../components/Header";
import ShoppingBreadcrumb from "../components/ShoppingBreadcrumb";
import BottomFooter from "../components/BottomFooter";
import CartSection from "../components/CartSection";
import ThankYouPopup from "../components/ThankYouPopup";
import ScrollToTop from "react-scroll-to-top";
import NewArrivalOne from "../components/NewArrivalOne";
import Spacer from "../components/common/Spacer";
import "./CartPage.css";
const EMPTY_CART_DATA = {};

const getItemId = (item, keys) => {
  const matchedKey = keys.find(
    (key) => item?.[key] !== null && item?.[key] !== undefined && item?.[key] !== ""
  );

  return matchedKey ? item[matchedKey] : null;
};

const parseJsonValue = (value) => {
  if (typeof value !== "string") return value;

  try {
    return JSON.parse(value);
  } catch (error) {
    return value;
  }
};

const isPlainObject = (value) =>
  value && typeof value === "object" && !Array.isArray(value);

const getFirstValue = (sources, keys, fallback = "") => {
  for (const source of sources) {
    const parsedSource = parseJsonValue(source);

    if (!isPlainObject(parsedSource)) continue;

    const value = getItemId(parsedSource, keys);
    if (value !== null && value !== undefined && value !== "") return value;
  }

  return fallback;
};

const getArrayValue = (sources, keys) => {
  for (const source of sources) {
    const parsedSource = parseJsonValue(source);

    if (!isPlainObject(parsedSource)) continue;

    for (const key of keys) {
      const parsedValue = parseJsonValue(parsedSource?.[key]);
      if (Array.isArray(parsedValue)) return parsedValue;
    }
  }

  return [];
};

const SEGMENT_CONTEXT_KEYS = ["segment_id", "segmentId", "segment"];
const BUSINESS_SEGMENT_CONTEXT_KEYS = [
  "business_segment_id",
  "businessSegmentId",
  "business_segment",
  "businessSegment",
  "store_id",
  "storeId",
  "branch_id",
];
const CART_ITEMS_KEYS = [
  "product_details",
  "product_detail",
  "products",
  "items",
  "cart_items",
  "cart_products",
  "cart_product_details",
  "response_data",
  "order_details",
  "details",
];

const CartPage = () => {
    const dispatch = useDispatch();
    const cartState = useSelector((state) => state.cart || {});
    const cartData = cartState?.data || EMPTY_CART_DATA;
    const cartId =
      cartState?.cartId ||
      cartData?.cart_id ||
      cartData?.cartId ||
      cartData?.id;

    const cartItems = useMemo(() => {
      const reduxCartItems = Array.isArray(cartState?.cartItems) ? cartState.cartItems : [];

      return reduxCartItems.length ? reduxCartItems : getArrayValue([cartData], CART_ITEMS_KEYS);
    }, [cartData, cartState?.cartItems]);

    const [cartRelatedItems, setCartRelatedItems] = useState(null);
    const [cartRelatedLoading, setCartRelatedLoading] = useState(true);
    const [initialCartLoading, setInitialCartLoading] = useState(true);
    const [orderPlaced, setOrderPlaced] = useState(false);
    const initialCartRequest = useRef(null);

    useEffect(() => {
      let active = true;

      // Reuse the initial request when StrictMode replays the mount effect.
      if (!initialCartRequest.current) {
        initialCartRequest.current = dispatch(loadCartPageWithSaveProductCart());
      }

      initialCartRequest.current.catch(() => {
        // Verification failures clear stale products in the cart reducer.
      }).finally(() => {
        if (active) setInitialCartLoading(false);
      });

      return () => { active = false; };
    }, [dispatch]);

    const cartContext = useMemo(() => {
      const cartReceipt = parseJsonValue(cartData?.receipt);
      const cartContextSources = [cartData, cartReceipt, cartItems[0], cartState.lastSaveCartPayload];
      const selectedSegmentId = getFirstValue(cartContextSources, SEGMENT_CONTEXT_KEYS);
      const selectedBusinessSegmentId = getFirstValue(
        cartContextSources,
        BUSINESS_SEGMENT_CONTEXT_KEYS
      );

      return {
        cart_id: cartId,
        segment_id: selectedSegmentId,
        business_segment_id: selectedBusinessSegmentId,
      };
    }, [cartData, cartId, cartItems, cartState.lastSaveCartPayload]);

    useEffect(() => {
      if (initialCartLoading) return;

      if (!cartId) {
        setCartRelatedItems(null);
        setCartRelatedLoading(false);
        return;
      }

      let active = true;
      setCartRelatedItems(null);
      setCartRelatedLoading(true);
      const cartRelatedPayload = new FormData();
      cartRelatedPayload.append("cart_id", cartId);

      dispatch(getCartRelatedItemList(cartRelatedPayload)).then((cartRelatedResponse) => {
        if (active && cartRelatedResponse?.result) {
          setCartRelatedItems(cartRelatedResponse.data || cartRelatedResponse);
        }
      }).catch((error) => {
        if (active) {
          console.log("Cart related items API Error:", error?.message);
          setCartRelatedItems(null);
        }
      }).finally(() => {
        if (active) setCartRelatedLoading(false);
      });

      return () => { active = false; };
    }, [initialCartLoading, cartId, dispatch]);

  return (
    <div className="cart-page bg-white">
      {/* ColorInit */}
      <ColorInit color={true} />

      {/* ScrollToTop */}
      <ScrollToTop smooth color="#FA6400" />

      {/* Preloader */}
      <Preloader />

      {/* Header */}
      <Header category={true} />

      {/* Breadcrumb */}
      <ShoppingBreadcrumb page="cart" loading={initialCartLoading} />

      {/* CartSection */}
      {initialCartLoading ? (
        <section className="cart">
          <div className="container container-lg text-center py-40" role="status">
            Loading cart...
          </div>
        </section>
      ) : cartItems.length > 0 || cartState.saveCartLoading || cartState.getCartLoading ? (
        <CartSection cartState={cartState} onOrderPlaced={() => setOrderPlaced(true)} />
      ) : (
        <section className="cart-page__empty">
          <div className="cart-page__empty-content">
            <span className="w-72 h-72 rounded-circle bg-gray-50 flex-center text-4xl text-gray-500 mb-16" aria-hidden="true">
              <i className="ph ph-shopping-cart-simple" />
            </span>
            <h5 className="mb-8">Cart empty</h5>
            <p className="text-gray-500 mb-0">Add a product and your basket will be ready.</p>
          </div>
        </section>
      )}
      <ThankYouPopup show={orderPlaced} onClose={() => setOrderPlaced(false)} />

      {/*NewArrivalOne*/}
      {!initialCartLoading && cartId && (
        <>
          <NewArrivalOne
            recentlybrowse={cartRelatedItems}
            loading={cartRelatedLoading}
            cartContext={cartContext}
          />
          <Spacer height={96} targetSelector=".place-button-section" gap={16} className="cart-page__bottom-spacer" />
        </>
      )}

      {/* BottomFooter */}
      {/* <BottomFooter /> */}


    </div>
  );
};

export default CartPage;
